import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { Text, Title, Card } from "@constants/Themed";
import styles from "@stylesheet";
import DefaultScreen from "@components/Global/DefaultScreenView";
import { useQuery } from "@tanstack/react-query";
import apis, { getDataFavorites } from "@api/api";
import DataOptionsModal, { DataOptionsModalHandle } from '@components/modals/DataOptionsModal/DataOptionsModal';
import ShareUserModal, { ShareConfirmHandle } from "@components/modals/ShareUserModal/ShareUserModal";
import ActionsModal, { ActionModalHandle } from '@components/modals/ActionsModal/ActionsModal';
import DataDeleteConfirmModal, { DeleteConfirmModalHandle } from '@components/modals/DeleteConfirmModal/DataDeleteConfirmModal';
import getId from "@api/getId";
import parseByDelimiter from "@utils/parseByDelimiter";
import { FlatList, RefreshControl, View, useWindowDimensions, Platform, StyleSheet, Pressable } from "react-native";
import AnimatedLottieView from "@utils/lottieAnimation";
import LottieView from "@utils/lottieAnimation";
import NetInfo from '@react-native-community/netinfo';
import RenderDataItems from '../components/DataItem/DataItem';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { filter, uniqueId } from "lodash";
import { YStack, XStack, Card as TamaguiCard } from "tamagui";
import { FontAwesome, FontAwesome5, Entypo, SimpleLineIcons, MaterialIcons, Ionicons } from '@expo/vector-icons';
import VirtualizedList from '../components/VirtualizedList';
import DataCounter from '../components/DataCounter';
import useGlobalUser from '@hooks/getGlobalUser';
import webTransition from '@utils/webTransition';
import { Loading } from "@components/Global/Loading";
import { debounce } from 'lodash';

const MAX_ITEM_WIDTH = 450;

const DataFavouritesScreen = ({ navigation }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState(windowWidth);
  const [activeItem, setActiveItem] = useState<any>({});
  const [refreshing, setRefreshing] = useState(false);
  const [searchState, setSearchState] = useState('');
  const [listHorizontal, setListHorizontal] = useState(false);
  const theme = themeSelector();
  const selectedId = useGlobalUser();

  // Function to calculate columns based on container width - matches ProjectManagementScreen
  const calculateColumns = useCallback((parentWidth: number) => {
    if (!parentWidth || parentWidth <= 0) return 1;
    // Calculate number of columns based on available width and item width
    // Add some padding/margin considerations (e.g., 40px container padding + 20px gap between items)
    const containerPadding = 40; // 20px on each side
    const itemGap = 20; // Gap between items
    const availableWidth = parentWidth - containerPadding;

    // Calculate how many items can fit with gaps
    // Formula: availableWidth >= (columns * MAX_ITEM_WIDTH) + ((columns - 1) * gap)
    // Rearranged: columns = (availableWidth + gap) / (MAX_ITEM_WIDTH + gap)
    const calculatedColumns = Math.floor((availableWidth + itemGap) / (MAX_ITEM_WIDTH + itemGap));

    // Always ensure at least 1 column, maximum reasonable columns for screen size
    const finalColumns = Math.max(1, Math.min(calculatedColumns, 6));

    return finalColumns;
  }, []);

  const [columns, setColumns] = useState(() => {
    if (windowWidth > 0) {
      const containerPadding = 40;
      const itemGap = 20;
      const availableWidth = windowWidth - containerPadding;
      const calculatedColumns = Math.floor((availableWidth + itemGap) / (MAX_ITEM_WIDTH + itemGap));
      return Math.max(1, Math.min(calculatedColumns, 6));
    }
    return 1;
  });

  // Modern modal refs
  const shareUserRef = useRef<ShareConfirmHandle>(null);
  const dataOptionsRef = useRef<DataOptionsModalHandle>(null);
  const actionsRef = useRef<ActionModalHandle>(null);
  const deleteConfirmRef = useRef<DeleteConfirmModalHandle>(null);

  const { status, data: favouriteData, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['favouriteData', selectedId],
    queryFn: async () => {
      console.log('📊 Fetching favourite data for user:', selectedId);
      const data = await getDataFavorites();
      console.log('✅ Favourite data fetched:', data?.length, 'items');
      return data || [];
    },
    retry: 2,
    staleTime: 1000 * 60, // Reduced to 1 minute for more frequent updates
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
    enabled: !!selectedId, // Only run query if we have a user ID
  });

  const processedData = useMemo(() => {
    if (!favouriteData?.length) return [];
    if (!searchState) return favouriteData;

    const formattedQuery = searchState.toLowerCase().replace(/ /g, '_');
    return filter(favouriteData, (item) =>
      item.dataTitle?.toLowerCase().includes(formattedQuery) || item.dataTags?.includes(formattedQuery)
    );
  }, [searchState, favouriteData]);

  // Update columns when window width or container width changes
  useEffect(() => {
    const debounceResize = debounce(() => {
      const parentWidth = containerWidth || windowWidth;
      if (parentWidth <= 0) return;

      const calculatedColumns = calculateColumns(parentWidth);
      setColumns(calculatedColumns);
      setListHorizontal(false);
    }, 100);

    debounceResize();

    return () => {
      debounceResize.cancel();
    };
  }, [containerWidth, windowWidth, calculateColumns]);

  const onLayout = useCallback((event) => {
    const { width } = event;
    try {
      if (width > 0 && width !== containerWidth) {
        setContainerWidth(width);
      }
    } catch (e) {
      console.error("Error setting container width:", e);
    }
  }, [containerWidth]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetch().finally(() => setRefreshing(false));
  }, [refetch]);

  // Modern modal handlers
  const handleShareModal = useCallback((item) => {
    setActiveItem(item); 
    shareUserRef.current?.openModal(item);
  }, []);

  const handleSettingsModal = useCallback((item) => {
    setActiveItem(item); 
    dataOptionsRef.current?.openModal(item);
  }, []);

  const handleDeleteModal = useCallback((item) => deleteConfirmRef.current?.openModal(item), []);
  const handleActionModal = useCallback((item) => actionsRef.current?.openModal(item), []);

  const renderVirtuosoItem = useCallback((index) => {
    const item = processedData[index];
    if (!item) return null;

    const itemStyle = {
      margin: 5,
      ...(Platform.OS === 'web' ? {
        width: listHorizontal ? containerWidth : (containerWidth / columns) - 10,
        ...webTransition('width, height', '200ms'),
      } : {
        flex: 1,
      })
    };

    return (
      <View style={itemStyle} key={item._id || uniqueId('favourite-')}>
        <RenderDataItems
          availableWidth={Platform.OS === 'web' && !listHorizontal ? (containerWidth / columns) : containerWidth}
          handleShareModal={handleShareModal}
          handleSettingsModal={handleSettingsModal}
          handleDeleteModal={handleDeleteModal}
          handleActionModal={handleActionModal}
          handleRefresh={refetch}
          item={item}
          index={index}
          listHorizontal={listHorizontal}
          handleShareState={handleShareModal}
        />
      </View>
    );
  }, [processedData, containerWidth, columns, listHorizontal, refetch, handleShareModal, handleSettingsModal, handleDeleteModal, handleActionModal]);

  const ListEmptyComponent = useMemo(() => () => (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 30, gap: 30 }}>
      {searchState ? (
        <View style={[styles.noteContainer, { padding: 20, alignItems: 'center' }]}>
          <LottieView source={require('@assets/animations/88006-warning-yellow.json')} autoPlay loop={false} style={{ width: '80%', height: 200 }} />
          <Text>No favourite items found matching your query.</Text>
        </View>
      ) : (
        <View style={[styles.centerEverything, { gap: 30, flex: 1, maxWidth: "100%", alignSelf: "stretch" }]}>
            <TamaguiCard padding={40} top={-300} borderRadius={20} backgroundColor={theme ? "#f8fafc" : "#1e293b"}>
            <YStack alignItems="center" gap={20}>
              <View style={{
                width: 80,
                height: 80,
                backgroundColor: '#f59e0b',
                borderRadius: 20,
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <FontAwesome name="heart" size={40} color="white" />
              </View>
                <Text style={{ fontSize: 24, fontWeight: '700', textAlign: 'center' }}>
                  No Favourites Yet
                </Text>
                <Text style={{ fontSize: 16, opacity: 0.7, textAlign: 'center', maxWidth: 300 }}>
                  Your favourite data models will appear here. Star items from your data collection to add them to favourites.
                </Text>
                <Pressable
                  style={{
                    backgroundColor: '#f59e0b',
                    paddingHorizontal: 24,
                    paddingVertical: 12,
                    borderRadius: 12,
                    marginTop: 10
                  }}
                  onPress={() => navigation.navigate('CaptureScreen')}
                >
                  <Text style={{ color: 'white', fontWeight: '600' }}>Browse Data Models</Text>
                </Pressable>
            </YStack>
          </TamaguiCard>
        </View>
      )}
    </View>
  ), [searchState, theme, navigation]);

  const VirtualList = () => {
    return (
      <VirtualizedList
        data={processedData}
        itemContent={renderVirtuosoItem}
        horizontal={true}
        components={{
          Header: processedData.length > 0 ? VirtuosoHeader : undefined,
        }}
        refreshing={refreshing}
        onRefresh={onRefresh}
        style={{ flex: 1 }}
      />
    );
  };

  const VirtuosoHeader = () => (
    <View style={{ margin: 20, borderRadius: 10 }}>
      {/* <XStack paddingBottom={15} overflowX='auto' gap={15}>
        <TamaguiCard
          width={350}
          padding={20}
          borderRadius={12}
          backgroundColor="#f59e0b"
          shadowColor="#000"
          shadowOffset={{ width: 0, height: 4 }}
          shadowOpacity={0.3}
          shadowRadius={8}
          elevation={8}
        >
          <YStack gap={12}>
            <View style={{
              width: 64,
              height: 64,
              backgroundColor: '#d97706',
              borderRadius: 12,
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <FontAwesome name="heart" size={28} color="white" />
            </View>
            <Text style={{ fontSize: 18, fontWeight: '600', color: 'white' }}>
              Favourite Data Models
            </Text>
            <Text style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 20 }}>
              You have {processedData?.length || 0} favourite models with quick access to your most important data.
            </Text>
            <XStack gap={20} marginTop={8}>
              <XStack alignItems="center" gap={4}>
                <FontAwesome name="heart" size={14} color="#fef3c7" />
                <Text style={{ color: '#fef3c7', fontSize: 12, fontWeight: '500' }}>
                  {processedData?.length || 0} Favourites
                </Text>
              </XStack>
              <XStack alignItems="center" gap={4}>
                <FontAwesome5 name="database" size={14} color="#fef3c7" />
                <Text style={{ color: '#fef3c7', fontSize: 12, fontWeight: '500' }}>
                  {processedData.reduce((acc, item) => acc + (item?.count || 0), 0)} Records
                </Text>
              </XStack>
            </XStack>
          </YStack>
        </TamaguiCard>
      </XStack> */}
    </View>
  );

  return (
    <DefaultScreen
      layoutCallback={onLayout}
      scrollDisabled
      screenTitle={'Favourite Data'}
      backButton
      noMargin
      containerStyle={{ flex: 1, paddingBottom: 40, width: '100%' }}
      searchBar={{
        callback: setSearchState,
        searchState: searchState,
        customIcons: [
          { function: refetch, icon: <FontAwesome name="refresh" size={18} color={"white"} />, functionName: 'refresh' },
          { function: () => setListHorizontal(prev => !prev), icon: listHorizontal ? <FontAwesome5 name="th-list" size={18} color={"white"} /> : <SimpleLineIcons name="grid" size={18} color={"white"} />, functionName: 'changeView' },
          { function: () => navigation.navigate('CaptureScreen'), icon: <FontAwesome name="database" size={18} color="white" />, functionName: 'browseData' }
        ]
      }}
    >
      {/* Modern Modal Components */}
      <DataOptionsModal ref={dataOptionsRef} title={activeItem?.dataTitle || ""} refreshFunction={refetch} />
      <ActionsModal ref={actionsRef} />
      <DataDeleteConfirmModal ref={deleteConfirmRef} deleteFunction={apis.deleteData} refreshFunction={refetch} />
      <ShareUserModal ref={shareUserRef} item={activeItem} shareFunction={apis.shareDataItem} />

      {isLoading ? (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: "center" }}>
          <Loading />
        </View>
      ) : (
        <View style={{ flex: 1 }}>
          {listHorizontal ? (
              <VirtualList />
          ) : (
            <VirtualizedList
              data={processedData}
              itemContent={renderVirtuosoItem}
              horizontal={false}
              containerWidth={containerWidth}
              columns={columns}
              components={{
                Header: processedData.length > 0 ? VirtuosoHeader : undefined,
              }}
              refreshing={refreshing}
              onRefresh={onRefresh}
              style={{ flex: 1 }}
              totalCount={processedData.length}
              computeItemKey={(index) => processedData[index]?._id || `item-${index}`}
            />
            )}
            {!processedData?.length && !searchState && (
              <ListEmptyComponent />
            )}
          </View>
      )}
    </DefaultScreen>
  );
}
export default DataFavouritesScreen;